import React, { useEffect, createElement } from 'react';
import { BookOpen, Clock, Check, Star, Download, ShieldCheck, ArrowRight, AlertCircle, Users } from 'lucide-react';
import { PurchaseNotification } from '../components/PurchaseNotification';
import { TestimonialCard } from '../components/TestimonialCard';
import { UrgencyBar } from '../components/UrgencyBar';
import { CheckoutSelector } from '../components/CheckoutSelector';
import { SecurePaymentBadge } from '../components/SecurePaymentBadge';
import { GuaranteeSection } from '../components/GuaranteeSection';
import { FAQSection } from '../components/FAQSection';
declare global {
  interface Window {
    fbq: any;
    _fbq: any;
  }
}
export function HistoriaLandingPage() {
  useEffect(() => {
    document.title = 'PACK HISTORIA';
    // Open Graph meta tags for link sharing
    const ogTags = [{
      property: 'og:title',
      content: 'PACK HISTORIA'
    }, {
      property: 'og:description',
      content: 'Pack completo con 5 tomos + actividades + evaluaciones + planificación anual para docentes de Historia.'
    }, {
      property: 'og:image',
      content: 'https://cdn.magicpatterns.com/uploads/vN5NBsmFmpkmg72E4pXW3r/HISTORIA_I_Historia_Antigua_y_Origenes_de_la_Humanidad_(4)_(1).png'
    }, {
      property: 'og:type',
      content: 'website'
    }];
    ogTags.forEach(({
      property,
      content
    }) => {
      let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    });
    const pixelId = '1537431667546082';
    if (!window.fbq) {
      const n = window.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      } as any;
      if (!window._fbq) window._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
    }
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(script);
    window.fbq('init', pixelId);
    window.fbq('track', 'PageView');
    const noscript = document.createElement('noscript');
    const img = document.createElement('img');
    img.height = 1;
    img.width = 1;
    img.style.display = 'none';
    img.src = `https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`;
    noscript.appendChild(img);
    document.body.appendChild(noscript);
    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);
  const scrollToPurchase = () => {
    const element = document.getElementById('purchase-section');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <div className="min-h-screen bg-[#F4E8D8] font-sans text-[#2D241E] overflow-x-hidden selection:bg-[#7C2D2D] selection:text-[#F4E8D8] pb-20 lg:pb-0" data-id="element-238">
      <PurchaseNotification data-id="element-239" />
      <UrgencyBar data-id="element-240" />

      {/* Navigation Bar */}
      <nav className="bg-[#2D241E] text-[#F4E8D8] py-3 px-4 shadow-md border-b-4 border-[#7C2D2D]" data-id="element-241">
        <div className="max-w-6xl mx-auto flex justify-between items-center" data-id="element-242">
          <div className="font-serif font-bold text-xl tracking-wider flex items-center gap-2" data-id="element-243">
            <BookOpen className="w-5 h-5" data-id="element-244" />
            <span data-id="element-245">HISTORIA DOCENTE</span>
          </div>
          <button onClick={scrollToPurchase} className="bg-[#1B7A4E] hover:bg-[#15633F] text-[#F4E8D8] px-4 py-1.5 rounded-sm font-serif font-bold text-sm transition-colors border border-[#F4E8D8]/30" data-id="element-246">
            DESCARGAR PACK
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <header className="relative pt-12 pb-20 px-4 overflow-hidden" data-id="element-247">
        <div className="absolute inset-0 opacity-5 bg-[url('https://www.transparenttextures.com/patterns/aged-paper.png')] pointer-events-none" data-id="element-248"></div>
        <div className="max-w-5xl mx-auto text-center relative z-10" data-id="element-249">
          <div className="inline-block border-b-2 border-[#7C2D2D] pb-1 mb-6" data-id="element-250">
            <span className="font-serif text-[#7C2D2D] font-bold tracking-widest uppercase text-sm" data-id="element-251">
              Para docentes de nivel secundario
            </span>
          </div>
          <h1 className="font-serif text-4xl md:text-6xl font-bold text-[#2D241E] mb-6 leading-tight" data-id="element-252">
            Todo el año de Historia, <br data-id="element-253" />
            <span className="text-[#7C2D2D] italic" data-id="element-254">
              planificado y listo para usar
            </span>
          </h1>
          <p className="font-sans text-lg md:text-xl text-[#5D4037] max-w-2xl mx-auto mb-6 leading-relaxed" data-id="element-255">
            Pack completo con 5 tomos + actividades + evaluaciones +
            planificación anual. Descargá hoy, usalo mañana.
          </p>

          <div className="flex items-center justify-center gap-2 mb-10 text-[#8B4513] font-medium bg-[#FFF8E7] py-2 px-4 rounded-full inline-flex mx-auto border border-[#8B4513]/20 shadow-sm" data-id="element-256">
            <Users className="w-5 h-5" data-id="element-257" />
            <span data-id="element-258">Ya lo usan +300 docentes en todo el país</span>
          </div>

          <div className="flex flex-col md:flex-row justify-center items-center gap-8 mb-16" data-id="element-259">
            <div className="w-full max-w-md transform hover:scale-105 transition-transform duration-500 cursor-pointer" onClick={scrollToPurchase} data-id="element-260">
              <img src="https://cdn.magicpatterns.com/uploads/vN5NBsmFmpkmg72E4pXW3r/HISTORIA_I_Historia_Antigua_y_Origenes_de_la_Humanidad_(4)_(1).png" alt="Pack Historia Integral" className="w-full drop-shadow-2xl rounded-sm border-4 border-[#FFF8E7]" data-id="element-261" />
            </div>
          </div>
          <button onClick={scrollToPurchase} className="group bg-[#1B7A4E] text-[#F4E8D8] text-xl md:text-2xl px-8 py-4 rounded-sm font-serif font-bold shadow-lg hover:bg-[#15633F] hover:shadow-xl transition-all border-2 border-[#15633F] flex items-center gap-3 mx-auto" data-id="element-262">
            <span data-id="element-263">QUIERO RECUPERAR MI TIEMPO</span>
            <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" data-id="element-264" />
          </button>
        </div>
      </header>

      {/* Problem Section */}
      <section className="py-20 px-4 bg-[#FFF8E7] border-y border-[#8B4513]/20 relative" data-id="element-265">
        <div className="max-w-3xl mx-auto" data-id="element-266">
          <div className="prose prose-lg prose-stone mx-auto font-sans text-[#5D4037]" data-id="element-267">
            <p className="font-serif text-2xl text-[#2D241E] font-bold mb-6 border-l-4 border-[#7C2D2D] pl-4" data-id="element-268">
              Clases que preparar. Contenidos que ordenar. Actividades que
              pensar. Evaluaciones que armar.
            </p>
            <p className="mb-4" data-id="element-269">
              Y muchas veces, todo eso fuera del horario escolar. Domingos
              perdidos armando clases. Semanas empezando sin saber bien qué dar.
              La sensación de que siempre falta algo.
            </p>
            <p className="mb-8 font-medium text-[#2D241E]" data-id="element-270">
              Cada año es lo mismo.
            </p>
            <p className="mb-8" data-id="element-271">
              No porque no sepas enseñar. No porque no tengas experiencia. Sino
              porque planificar bien lleva tiempo.
              <span className="font-bold text-[#7C2D2D]" data-id="element-272">
                {' '}
                Tiempo que casi nunca sobra.
              </span>
            </p>
            <h2 className="font-serif text-3xl font-bold text-[#2D241E] mt-12 mb-6 text-center" data-id="element-273">
              El verdadero problema no es dar clases
            </h2>
            <p className="text-center text-xl italic text-[#8B4513] mb-8" data-id="element-274">
              El problema es tener que organizarlo todo solo, cada año.
            </p>
            <ul className="space-y-4 mb-10 bg-[#F4E8D8] p-8 rounded-sm border border-[#8B4513]/30" data-id="element-275">
              <li className="flex items-start gap-3" data-id="element-276">
                <span className="text-[#7C2D2D] font-bold text-xl" data-id="element-277">✗</span>
                <span data-id="element-278">Buscar materiales dispersos.</span>
              </li>
              <li className="flex items-start gap-3" data-id="element-279">
                <span className="text-[#7C2D2D] font-bold text-xl" data-id="element-280">✗</span>
                <span data-id="element-281">Unir PDFs que no tienen coherencia.</span>
              </li>
              <li className="flex items-start gap-3" data-id="element-282">
                <span className="text-[#7C2D2D] font-bold text-xl" data-id="element-283">✗</span>
                <span data-id="element-284">Adaptar textos demasiado complejos.</span>
              </li>
              <li className="flex items-start gap-3" data-id="element-285">
                <span className="text-[#7C2D2D] font-bold text-xl" data-id="element-286">✗</span>
                <span data-id="element-287">Reescribir consignas a última hora.</span>
              </li>
            </ul>
            <p className="text-lg font-medium" data-id="element-288">
              Y aun así, entrar al aula con la sensación de que algo falta. No
              falta compromiso. No falta vocación.
            </p>
            <div className="mt-8 text-center" data-id="element-289">
              <span className="inline-block bg-[#2D241E] text-[#F4E8D8] px-6 py-2 font-serif font-bold text-xl rounded-sm transform -rotate-1" data-id="element-290">
                👉 Falta orden.
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 px-4 bg-[#2D241E] text-[#F4E8D8] relative overflow-hidden" data-id="element-291">
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/shattered-island.png')]" data-id="element-292"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-293">
          <h2 className="font-serif text-3xl md:text-5xl font-bold mb-8 text-[#F4E8D8]" data-id="element-294">
            ¿Y si ese trabajo ya estuviera hecho?
          </h2>
          <p className="text-xl mb-12 text-[#FFF8E7]/90 max-w-2xl mx-auto" data-id="element-295">
            Imaginá abrir un archivo y encontrar:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 text-left" data-id="element-296">
            {['Los contenidos organizados por períodos históricos', 'Lecciones claras y coherentes', 'Actividades listas para usar', 'Líneas de tiempo y esquemas visuales', 'Propuestas de evaluación ya pensadas'].map((item, index) => <div key={index} className="bg-[#3E3228] p-6 rounded-sm border border-[#8B4513] hover:border-[#7C2D2D] transition-colors" data-id="element-297">
                <Check className="w-8 h-8 text-[#7C2D2D] mb-4" data-id="element-298" />
                <p className="font-serif text-lg" data-id="element-299">{item}</p>
              </div>)}
          </div>
          <div className="mt-16 bg-[#F4E8D8] text-[#2D241E] p-8 md:p-12 rounded-sm shadow-2xl border-4 border-[#7C2D2D] max-w-3xl mx-auto transform rotate-1" data-id="element-300">
            <p className="font-sans font-bold text-[#7C2D2D] tracking-widest uppercase mb-4 text-sm" data-id="element-301">
              TODO EN UN MISMO LUGAR
            </p>
            <h3 className="font-serif text-3xl md:text-4xl font-bold mb-4" data-id="element-302">
              PACK HISTORIA PARA DOCENTES
            </h3>
            <p className="font-serif text-xl md:text-2xl italic text-[#8B4513] mb-8" data-id="element-303">
              Nivel Secundario
            </p>
            <p className="font-sans text-lg mb-8" data-id="element-304">
              Un Pack Integral de Historia, creado para docentes que necesitan:
            </p>
            <div className="flex flex-wrap justify-center gap-4 font-bold text-[#5D4037]" data-id="element-305">
              <span className="flex items-center gap-2" data-id="element-306">
                <Check className="w-5 h-5 text-[#7C2D2D]" data-id="element-307" /> Ahorrar tiempo real
              </span>
              <span className="flex items-center gap-2" data-id="element-308">
                <Check className="w-5 h-5 text-[#7C2D2D]" data-id="element-309" /> Ordenar su
                planificación
              </span>
              <span className="flex items-center gap-2" data-id="element-310">
                <Check className="w-5 h-5 text-[#7C2D2D]" data-id="element-311" /> Tener material
                listo
              </span>
              <span className="flex items-center gap-2" data-id="element-312">
                <Check className="w-5 h-5 text-[#7C2D2D]" data-id="element-313" /> Enseñar con
                tranquilidad
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Product Details - Cuadernillos */}
      <section className="py-20 px-4 bg-[#F4E8D8]" data-id="element-314">
        <div className="max-w-5xl mx-auto" data-id="element-315">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-center text-[#2D241E] mb-16" data-id="element-316">
            Este pack reúne cinco cuadernillos digitales,
            <br data-id="element-317" />
            <span className="text-[#7C2D2D]" data-id="element-318">
              organizados por grandes períodos históricos:
            </span>
          </h2>
          <div className="space-y-6" data-id="element-319">
            {[{
            title: 'Historia I',
            subtitle: 'Historia Antigua y Orígenes de la Humanidad'
          }, {
            title: 'Historia II',
            subtitle: 'Historia Medieval y Transición a la Modernidad'
          }, {
            title: 'Historia III',
            subtitle: 'Edad Moderna y Formación del Mundo Occidental'
          }, {
            title: 'Historia IV',
            subtitle: 'Historia Contemporánea – Siglos XIX y XX'
          }, {
            title: 'Historia V',
            subtitle: 'Historia Argentina y Latinoamericana'
          }].map((book, idx) => <div key={idx} className="flex items-center gap-6 bg-[#FFF8E7] p-6 rounded-sm border-l-8 border-[#7C2D2D] shadow-sm hover:shadow-md transition-shadow" data-id="element-320">
                <div className="bg-[#2D241E] text-[#F4E8D8] w-12 h-12 flex items-center justify-center rounded-full font-serif font-bold text-xl flex-shrink-0" data-id="element-321">
                  {idx + 1}
                </div>
                <div data-id="element-322">
                  <h3 className="font-serif font-bold text-xl text-[#7C2D2D]" data-id="element-323">
                    {book.title}
                  </h3>
                  <p className="font-sans text-[#5D4037] font-medium" data-id="element-324">
                    {book.subtitle}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 bg-[#FFF8E7] border-y border-[#8B4513]/20" data-id="element-325">
        <div className="max-w-4xl mx-auto text-center" data-id="element-326">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#2D241E] mb-12" data-id="element-327">
            ¿Qué cambia cuando trabajás con este pack?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16" data-id="element-328">
            <div className="bg-[#F4E8D8] p-6 rounded-sm border border-[#8B4513]/20" data-id="element-329">
              <Clock className="w-10 h-10 text-[#7C2D2D] mx-auto mb-4" data-id="element-330" />
              <h3 className="font-serif font-bold text-xl mb-2" data-id="element-331">
                Dejás de planificar desde cero
              </h3>
              <p className="text-[#5D4037]" data-id="element-332">
                Tu tiempo vale. Usalo para descansar o disfrutar, no para
                reinventar la rueda.
              </p>
            </div>
            <div className="bg-[#F4E8D8] p-6 rounded-sm border border-[#8B4513]/20" data-id="element-333">
              <ShieldCheck className="w-10 h-10 text-[#7C2D2D] mx-auto mb-4" data-id="element-334" />
              <h3 className="font-serif font-bold text-xl mb-2" data-id="element-335">
                Ganás seguridad al entrar al aula
              </h3>
              <p className="text-[#5D4037]" data-id="element-336">
                Sabés exactamente qué dar y cómo encararlo en cada clase.
              </p>
            </div>
            <div className="bg-[#F4E8D8] p-6 rounded-sm border border-[#8B4513]/20" data-id="element-337">
              <BookOpen className="w-10 h-10 text-[#7C2D2D] mx-auto mb-4" data-id="element-338" />
              <h3 className="font-serif font-bold text-xl mb-2" data-id="element-339">
                Reutilizás el material
              </h3>
              <p className="text-[#5D4037]" data-id="element-340">
                Una inversión única que te sirve año tras año.
              </p>
            </div>
            <div className="bg-[#F4E8D8] p-6 rounded-sm border border-[#8B4513]/20" data-id="element-341">
              <Star className="w-10 h-10 text-[#7C2D2D] mx-auto mb-4" data-id="element-342" />
              <h3 className="font-serif font-bold text-xl mb-2" data-id="element-343">
                Recuperás energía mental
              </h3>
              <p className="text-[#5D4037]" data-id="element-344">
                Menos estrés por la planificación significa más energía para la
                enseñanza.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Bonuses Section - Sin imágenes */}
      <section className="py-20 px-4 bg-[#2D241E] text-[#F4E8D8] relative" data-id="element-345">
        <div className="absolute inset-0 opacity-5 bg-[url('https://www.transparenttextures.com/patterns/wood-pattern.png')]" data-id="element-346"></div>
        <div className="max-w-4xl mx-auto relative z-10" data-id="element-347">
          <div className="text-center mb-12" data-id="element-348">
            <span className="bg-[#7C2D2D] text-[#F4E8D8] px-4 py-1 font-bold rounded-full text-sm uppercase tracking-wider mb-4 inline-block" data-id="element-349">
              Oferta Especial Limitada
            </span>
            <h2 className="font-serif text-4xl md:text-5xl font-bold mb-4" data-id="element-350">
              Bonos Exclusivos
            </h2>
            <p className="font-sans text-xl text-[#F4E8D8]/80" data-id="element-351">
              Llevate todo esto{' '}
              <span className="font-bold text-[#1B7A4E] underline" data-id="element-352">GRATIS</span>{' '}
              con tu compra hoy
            </p>
          </div>
          <div className="space-y-4" data-id="element-353">
            {[{
            title: 'BONUS 1: Planificación Anual',
            description: 'Planificación Anual y Cuatrimestral. Organización completa del año lectivo con articulación de contenidos.'
          }, {
            title: 'BONUS 2: Líneas de Tiempo y Esquemas Visuales',
            description: 'Líneas de tiempo y esquemas visuales desarrollados para todos los tomos de este pack de historia.'
          }, {
            title: 'BONUS 3: ChatGPT IA para Docentes',
            description: 'Guía completa de ChatGPT para planificar y enseñar Historia. Prompts listos para copiar y pegar, creación de actividades didácticas y adaptaciones curriculares.'
          }].map((bonus, idx) => <div key={idx} className="flex items-start gap-4 bg-[#3E3228] border border-[#8B4513]/40 rounded-lg p-5" data-id="element-354">
                <div className="bg-[#1B7A4E] text-[#F4E8D8] w-10 h-10 flex items-center justify-center rounded-full font-serif font-bold text-lg flex-shrink-0" data-id="element-355">
                  {idx + 1}
                </div>
                <div className="flex-1" data-id="element-356">
                  <div className="flex items-center gap-3 mb-1" data-id="element-357">
                    <h3 className="font-serif font-bold text-lg text-[#F4E8D8]" data-id="element-358">
                      {bonus.title}
                    </h3>
                    <span className="bg-[#1B7A4E] text-[#F4E8D8] px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider flex-shrink-0" data-id="element-359">
                      Gratis
                    </span>
                  </div>
                  <p className="text-sm text-[#F4E8D8]/70 leading-relaxed" data-id="element-360">
                    {bonus.description}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Purchase Section */}
      <section id="purchase-section" className="py-24 px-4 bg-[#F4E8D8] relative" data-id="element-361">
        <div className="max-w-7xl mx-auto relative z-10" data-id="element-362">
          <div className="text-center mb-12" data-id="element-363">
            <h2 className="font-serif text-4xl md:text-5xl font-bold mb-6 text-[#2D241E]" data-id="element-364">
              Elegí el Combo que Mejor se Adapte a Vos
            </h2>
            <p className="text-xl text-[#5D4037] max-w-3xl mx-auto" data-id="element-365">
              Todos los materiales son digitales, descargables y de acceso
              inmediato tras la compra.
            </p>
          </div>

          <CheckoutSelector data-id="element-366" />

          <SecurePaymentBadge data-id="element-367" />

          <GuaranteeSection data-id="element-368" />

          {/* Testimonials - After Guarantee */}
          <div className="max-w-6xl mx-auto mt-20" data-id="element-369">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-center text-[#2D241E] mb-12" data-id="element-370">
              Lo que dicen quienes ya lo usan
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-371">
              <TestimonialCard name="Claudia Morales" age="42 años" role="Profesora de Historia" location="Buenos Aires" content="Antes pasaba los fines de semana buscando material. Ahora tengo todo organizado y puedo dedicarme a corregir tranquila. Mis alumnos notaron el cambio en la organización de las clases." data-id="element-372" />
              <TestimonialCard name="Silvina Romero" age="45 años" role="Profesora de Historia" location="Rosario" content="Tengo 6 cursos diferentes y me estaba volviendo loca. Este pack me dio el orden que necesitaba. Las actividades son muy buenas y adaptables a distintos grupos." data-id="element-373" />
              <TestimonialCard name="Patricia Vega" age="36 años" role="Profesora de Historia" location="La Plata" content="La mejor inversión que hice este año. Recuperé mis domingos. El material es súper completo y visualmente atractivo para los chicos. ¡Recomendadísimo!" data-id="element-374" />
            </div>
          </div>

          <div className="max-w-3xl mx-auto mt-16" data-id="element-375">
            <div className="bg-[#FFF8E7] text-[#2D241E] border-l-4 border-[#8B4513] p-6 rounded-sm shadow-sm mb-8" data-id="element-376">
              <div className="flex items-start gap-3" data-id="element-377">
                <span className="text-2xl flex-shrink-0" data-id="element-378">💡</span>
                <div className="text-sm" data-id="element-379">
                  <p className="font-bold text-[#7C2D2D] mb-2" data-id="element-380">Importante:</p>
                  <p className="leading-relaxed text-[#5D4037]" data-id="element-381">
                    Si no tenés cuenta en Mercado Pago, no te preocupes, lo
                    utilizamos como pasarela de pagos segura pero{' '}
                    <span className="font-semibold" data-id="element-382">
                      no es necesario tener cuenta
                    </span>{' '}
                    para efectuar la compra. Podés pagar igualmente con
                    cualquier tarjeta de crédito o débito.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center text-[#5D4037] text-sm font-sans mt-16" data-id="element-383">
            <div className="flex flex-col items-center" data-id="element-384">
              <div className="bg-[#FFF8E7] p-4 rounded-full mb-3 border border-[#8B4513]/20 shadow-sm" data-id="element-385">
                <Download className="w-6 h-6 text-[#7C2D2D]" data-id="element-386" />
              </div>
              <p className="font-bold" data-id="element-387">Material 100% digital (PDF)</p>
            </div>
            <div className="flex flex-col items-center" data-id="element-388">
              <div className="bg-[#FFF8E7] p-4 rounded-full mb-3 border border-[#8B4513]/20 shadow-sm" data-id="element-389">
                <Clock className="w-6 h-6 text-[#7C2D2D]" data-id="element-390" />
              </div>
              <p className="font-bold" data-id="element-391">Descarga inmediata</p>
            </div>
            <div className="flex flex-col items-center" data-id="element-392">
              <div className="bg-[#FFF8E7] p-4 rounded-full mb-3 border border-[#8B4513]/20 shadow-sm" data-id="element-393">
                <BookOpen className="w-6 h-6 text-[#7C2D2D]" data-id="element-394" />
              </div>
              <p className="font-bold" data-id="element-395">Apto para imprimir</p>
            </div>
          </div>
        </div>
      </section>

      <FAQSection data-id="element-396" />

      {/* Final CTA Section */}
      <section className="py-24 px-4 bg-[#2D241E] text-[#F4E8D8] border-t-4 border-[#7C2D2D] relative overflow-hidden" data-id="element-397">
        <div className="absolute inset-0 opacity-5 bg-[url('https://www.transparenttextures.com/patterns/aged-paper.png')] pointer-events-none" data-id="element-398"></div>
        <div className="max-w-3xl mx-auto text-center relative z-10" data-id="element-399">
          <h2 className="font-serif text-3xl md:text-5xl font-bold mb-6" data-id="element-400">
            Cada semana que pasa sin este pack, es otra semana planificando
            desde cero
          </h2>
          <p className="font-sans text-xl text-[#F4E8D8]/80 mb-10" data-id="element-401">
            Hay decisiones que se notan en el momento. Y otras que se agradecen
            durante todo el año.
            <br data-id="element-402" />
            <span className="font-bold italic text-[#F4E8D8]" data-id="element-403">
              Esta es una de esas.
            </span>
          </p>

          <button onClick={scrollToPurchase} className="group bg-[#1B7A4E] hover:bg-[#15633F] text-[#F4E8D8] text-xl md:text-2xl font-bold py-5 px-10 rounded-sm shadow-xl hover:shadow-2xl transition-all border-2 border-[#15633F] flex items-center justify-center gap-3 mx-auto w-full md:w-auto" data-id="element-404">
            <span data-id="element-405">QUIERO ORGANIZAR MI AÑO AHORA</span>
            <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" data-id="element-406" />
          </button>
          <p className="mt-6 text-sm text-[#F4E8D8]/60 font-medium tracking-wide uppercase" data-id="element-407">
            Descarga inmediata • Garantía 7 días • Pago seguro
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1A1410] text-[#F4E8D8]/60 py-12 px-4 text-center font-sans text-sm" data-id="element-408">
        <div className="max-w-4xl mx-auto" data-id="element-409">
          <div className="flex justify-center items-center gap-2 mb-6 opacity-80" data-id="element-410">
            <BookOpen className="w-6 h-6" data-id="element-411" />
            <span className="font-serif font-bold text-lg tracking-wider text-[#F4E8D8]" data-id="element-412">
              HISTORIA DOCENTE
            </span>
          </div>
          <p className="mb-4" data-id="element-413">
            Material didáctico profesional para docentes de nivel secundario.
          </p>
          <p data-id="element-414">
            &copy; {new Date().getFullYear()} Pack Historia Integral. Todos los
            derechos reservados.
          </p>
        </div>
      </footer>
    </div>;
}